import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import useTravelZmanim from "../../../services/useTravelZmanim";
import { formatTravelDay, formatZmanimDate, formatZmanimTime } from "../../../services/travelZmanim";
import { languageId } from "../../../services/localeCodes";
import { restPeriodTitle, restPeriodRowLabel } from "../../../services/restPeriodDisplay";

const zmanimStyles = `
.tr-zmanim__title {
  font-size: clamp(20px, 3.5vw, 32px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 64px 0 40px;
}

/* ── Sélecteur de date ── */
.tr-zmanim__nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
}
.tr-zmanim__nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1.5px solid #e5e7eb;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--primary, #1b579e);
  transition: background .15s;
  flex-shrink: 0;
}
.tr-zmanim__nav-btn:hover { background: #f0f4ff; }
.tr-zmanim__nav-label {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  min-width: 200px;
  text-align: center;
  line-height: 1.3;
}
.tr-zmanim__nav-label span {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: #6b7280;
  margin-top: 2px;
}

/* ── Carte zmanim du jour ── */
.tr-zmanim__card {
  max-width: 680px;
  margin: 0 auto 40px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  overflow: hidden;
}
.tr-zmanim__card-header {
  background: var(--primary, #1b579e);
  color: white;
  text-align: center;
  padding: 18px 24px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
}
.tr-zmanim__card-header span {
  display: block;
  font-size: 13px;
  font-weight: 400;
  opacity: .85;
  margin-top: 2px;
}
.tr-zmanim__list {
  padding: 0;
  margin: 0;
  list-style: none;
}
.tr-zmanim__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-bottom: 1px solid #f3f4f6;
  gap: 12px;
  transition: background .1s;
}
.tr-zmanim__item:last-child { border-bottom: none; }
.tr-zmanim__item--next {
  background: #eff6ff;
}
.tr-zmanim__item-name {
  font-size: 14px;
  color: #374151;
  flex: 1;
}
.tr-zmanim__item--next .tr-zmanim__item-name {
  color: var(--primary, #1b579e);
  font-weight: 600;
}
.tr-zmanim__item-badge {
  font-size: 11px;
  background: var(--primary, #1b579e);
  color: white;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
  white-space: nowrap;
}
.tr-zmanim__item-time {
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
  white-space: nowrap;
}
.tr-zmanim__item--next .tr-zmanim__item-time {
  color: var(--primary, #1b579e);
}

/* ── Carte Chabbat ── */
.tr-zmanim__shabbat-card {
  max-width: 680px;
  margin: 0 auto 60px;
  background: #eff6ff;
  border-radius: 20px;
  overflow: hidden;
  border: 1.5px solid #bfdbfe;
}
.tr-zmanim__shabbat-header {
  background: #1e40af;
  color: white;
  text-align: center;
  padding: 16px 24px;
  font-size: 15px;
  font-weight: 600;
}
.tr-zmanim__shabbat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #bfdbfe;
}
.tr-zmanim__shabbat-row:last-child { border-bottom: none; }
.tr-zmanim__shabbat-label {
  font-size: 14px;
  color: #374151;
}
.tr-zmanim__shabbat-label small { display: block; font-size: 11px; opacity: .72; margin-top: 2px; }
.tr-zmanim__shabbat-value {
  font-size: 18px;
  font-weight: 700;
  color: #1e40af;
}

@media (max-width: 640px) {
  .tr-zmanim__title { padding: 0 16px; }
  .tr-zmanim__card,
  .tr-zmanim__shabbat-card { margin-left: 16px; margin-right: 16px; border-radius: 14px; }
  .tr-zmanim__item { padding: 12px 16px; }
  .tr-zmanim__shabbat-row { padding: 14px 16px; }
}
`;


export default function JewishZmanim({ context, loading }) {
    const { org, config } = context;
    const locale = languageId(context.langValue || context.lang || org.language);
    const { day, timeZone, zmanim, hdate, restPeriod, goDay } = useTravelZmanim(org);

    /* Noms FR des zmanim hébreux */
    const ZMANIM_FR = {
        "עלות השחר 120 דקות": t`Aube (120 min)`,
        "עלות השחר 72 דקות":  t`Aube (72 min)`,
        "עלות השחר":          t`Aube`,
        "תפילין ושמע":        t`Tefillin & Chéma`,
        "נץ החמה":            t`Lever du soleil`,
        "סוף זמן ק״ש":        t`Heure (limite) du Kriat Chéma (Admour Hazaken)`,
        "סוף זמן ק״ש מ״א":    t`Heure (limite) du Kriat Chéma (Maguen Avraham)`,
        "סוף זמן ק״ש גר״א":   t`Heure (limite) du Kriat Chéma (Admour Hazaken)`,
        "סוף זמן תפילה":       t`Heure (limite) de la prière (Amida) (Gra)`,
        "סוף זמן תפילה מ״א":   t`Heure (limite) de la prière (Maguen Avraham)`,
        "סוף זמן תפילה גר״א":  t`Heure (limite) de la prière (Amida) (Gra)`,
        "חצות היום":           t`Hatsot (milieu de la journée)`,
        "מנחה גדולה":          t`Min'ha Gdola`,
        "מנחה קטנה":           t`Min'ha Ketana`,
        "פלג המנחה":           t`Plag haMinha`,
        "שקיעת החמה":          t`Coucher du soleil`,
        "צאת הכוכבים":         t`Nuit (sortie des étoiles)`,
        "צאת דאורייתא":        t`Nuit - Tsèt deoraïta`,
        "צאת הכוכבים ר'ת":     t`Nuit - Rabbenou Tam`,
        "חצות לילה":           t`Minuit halachique`,
    };

    return (
        <div className="travel-page travel-page--zmanim tr-zmanim">
            <style dangerouslySetInnerHTML={{ __html: zmanimStyles }} />
            <Meta
                context={context}
                title={t`Horaires halachiques — ${org.ville || org.name}`}
                description={config["meta.description"] || t`Zmanim, heures de prière et horaires halachiques quotidiens à ${org.ville || org.name}`}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-zmanim__title">
                        <Trans>Horaires Halachiques à</Trans> {org.ville || org.name}
                    </h1>

                    {/* ── Sélecteur de date ── */}
                    <div className="tr-zmanim__nav">
                        <button className="tr-zmanim__nav-btn" onClick={() => goDay(-1)}>‹</button>
                        <div className="tr-zmanim__nav-label">
                            {formatTravelDay(day, locale)}
                            {hdate && (
                                <span>{hdate.getDate()} {hdate.getMonthName()} {hdate.getFullYear()}</span>
                            )}
                        </div>
                        <button className="tr-zmanim__nav-btn" onClick={() => goDay(1)}>›</button>
                    </div>

                    {/* ── Carte zmanim du jour ── */}
                    <div className="tr-zmanim__card">
                        <div className="tr-zmanim__card-header">
                            {formatTravelDay(day, locale)}
                            {hdate && (
                                <span>
                                    {hdate.getDate()} {hdate.getMonthName()} {hdate.getFullYear()}
                                </span>
                            )}
                        </div>
                        <ul className="tr-zmanim__list">
                            {zmanim.map((z, i) => (
                                <li key={i} className={`tr-zmanim__item${z.next ? " tr-zmanim__item--next" : ""}`}>
                                    <span className="tr-zmanim__item-name">
                                        {ZMANIM_FR[z.nom] || z.nom}
                                    </span>
                                    {z.next && (
                                        <span className="tr-zmanim__item-badge">
                                            <Trans>Prochain</Trans>
                                        </span>
                                    )}
                                    <span className="tr-zmanim__item-time">
                                        {formatZmanimTime(z.time, locale, timeZone)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* ── Carte Chabbat à venir ── */}
                    {restPeriod && (
                        <div className="tr-zmanim__shabbat-card">
                            <div className="tr-zmanim__shabbat-header">
                                {restPeriodTitle(restPeriod, locale)}
                                <span> · {formatZmanimDate(restPeriod.start, locale, restPeriod.timeZone)} — {formatZmanimDate(restPeriod.end, locale, restPeriod.timeZone)}</span>
                            </div>
                            {restPeriod.schedule.map((item, index) => (
                                <div className="tr-zmanim__shabbat-row" key={index}>
                                    <span className="tr-zmanim__shabbat-label">
                                        {restPeriodRowLabel(item, restPeriod)}
                                        <small>{formatZmanimDate(item.at, locale, restPeriod.timeZone)}</small>
                                    </span>
                                    <span className="tr-zmanim__shabbat-value">
                                        {formatZmanimTime(item.at, locale, restPeriod.timeZone)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
